import { api } from "./authService";

const gradeService = {
  // Alle Noten abrufen
  getAllGrades: async () => {
    try {
      const response = await api.get("/grades");
      return response.data;
    } catch (error) {
      console.error("Error fetching grades:", error);
      throw error;
    }
  },

  // Note anhand ID abrufen
  getGradeById: async (id) => {
    try {
      const response = await api.get(`/grades/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching grade ${id}:`, error);
      throw error;
    }
  },

  // Noten für einen bestimmten Benutzer abrufen
  getGradesByUser: async (userId) => {
    try {
      const response = await api.get(`/grades/user/${userId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching grades for user ${userId}:`, error);
      throw error;
    }
  },

  // Noten für einen bestimmten Benutzer und Semester abrufen
  getGradesByUserAndSemester_deprecated: async (userId, semesterObj) => {
    try {
      // Extrahiere nur die benötigten Werte aus dem Semester-Objekt
      let semester, academicYear;
      
      if (typeof semesterObj === 'object' && semesterObj !== null) {
        semester = semesterObj.name || semesterObj.semester || 'HS2024';
        academicYear = semesterObj.academicYear || '2024/25';
      } else {
        semester = String(semesterObj); // Stelle sicher, dass es ein String ist
        academicYear = '2024/25';
      }

      // Explicit parameter object erstellen (nicht das ganze Objekt übergeben)
      const params = {
        semester: semester,
        academicYear: academicYear
      };

      const response = await api.get(`/grades/user/${userId}/semester`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching grades for user ${userId} and semester:`, error);
      // Fallback: alle Noten des Users abrufen und filtern
      try {
        const allGrades = await gradeService.getGradesByUser(userId);
        
        // Filter by semester name or ID
        const semesterName = typeof semesterObj === 'object' ? semesterObj.name : String(semesterObj);
        const semesterId = typeof semesterObj === 'object' ? semesterObj.id : semesterObj;
        
        const filtered = allGrades.filter(grade => 
          grade.semester === semesterName || 
          grade.semesterId === semesterId ||
          grade.semester === String(semesterObj) ||
          (grade.semester && grade.semester.includes(semesterName))
        );
        return filtered;
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        return [];
      }
    }
  },

  // Noten für ein bestimmtes Fach abrufen
  getGradesBySubject: async (subjectId) => {
    try {
      const response = await api.get(`/grades/subject/${subjectId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching grades for subject ${subjectId}:`, error);
      throw error;
    }
  },

  // Noten für einen bestimmten Benutzer und Semester ID abrufen (neue Methode)
  getGradesByUserAndSemesterId: async (userId, semesterId) => {
    try {
      const params = { semesterId: semesterId };
      const response = await api.get(`/grades/user/${userId}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching grades for user ${userId} and semesterId ${semesterId}:`, error);
      throw error;
    }
  },

  // Neue Note erstellen
  createGrade: async (gradeData) => {
    try {
      const response = await api.post("/grades", gradeData);
      return response.data;
    } catch (error) {
      console.error("❌ Error creating grade:", error);
      console.error("📋 Error details:");
      console.error("  - Status:", error.response?.status);
      console.error("  - Status Text:", error.response?.statusText);
      console.error("  - Response Data:", error.response?.data);
      console.error("  - Request Config:", error.config);
      
      // Try to get a more meaningful error message
      let errorMessage = "Failed to create grade";
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Create a new error with a better message
      const enhancedError = new Error(errorMessage);
      enhancedError.originalError = error;
      enhancedError.status = error.response?.status;
      enhancedError.responseData = error.response?.data;
      
      throw enhancedError;
    }
  },

  // Note aktualisieren
  updateGrade: async (id, gradeData) => {
    try {
      const response = await api.put(`/grades/${id}`, gradeData);
      return response.data;
    } catch (error) {
      console.error(`Error updating grade ${id}:`, error);
      throw error;
    }
  },

  // Note löschen
  deleteGrade: async (id) => {
    try {
      await api.delete(`/grades/${id}`);
    } catch (error) {
      console.error(`Error deleting grade ${id}:`, error);
      throw error;
    }
  },

  // Durchschnittsnote für einen Benutzer berechnen
  calculateUserAverage: async (userId) => {
    try {
      const response = await api.get(`/grades/user/${userId}/average`);
      return response.data;
    } catch (error) {
      console.error(`Error calculating average for user ${userId}:`, error);
      throw error;
    }
  },

  // Durchschnittsnote für ein Fach berechnen
  calculateSubjectAverage: async (subjectId) => {
    try {
      const response = await api.get(`/grades/subject/${subjectId}/average`);
      return response.data;
    } catch (error) {
      console.error(`Error calculating average for subject ${subjectId}:`, error);
      throw error;
    }
  },

  // Noten für ein spezifisches Semester abrufen (neue Methode)
  getGradesBySemester: async (semesterId) => {
    try {
      // Fallback: Alle Noten abrufen und clientseitig nach Semester filtern
      const response = await api.get(`/grades`);
      const allGrades = response.data;
      
      // Lade Benutzer-Info um die User ID zu bekommen
      const userResponse = await api.get('/auth/profile');
      const currentUser = userResponse.data;
      
      // Filtere Noten nach aktueller User ID und Semester ID
      const semesterGrades = allGrades.filter(grade => 
        grade.userId === currentUser.id && 
        grade.semesterId === semesterId
      );
      return semesterGrades;
    } catch (error) {
      console.error(`Error fetching grades for semester ${semesterId}:`, error);
      throw error;
    }
  },

  // Meine eigenen Noten abrufen (für Lernende)
  getMyGrades: async (semesterId = null) => {
    try {
      // Use the semester-specific endpoint if semesterId is provided
      let url = '/grades/my-grades';
      if (semesterId) {
        url += `?semesterId=${semesterId}`;
      }
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching my grades:", error);
      throw error;
    }
  },

  // US15: Grade Calculation per Subject & Semester
  getSubjectAverage: async (userId, subjectId, semester, academicYear) => {
    try {
      // If semester is an object, extract the name and schoolYear
      let semesterName, academicYearParam;
      if (typeof semester === 'object' && semester !== null) {
        semesterName = semester.name;
        academicYearParam = semester.schoolYear || academicYear;
      } else {
        semesterName = semester;
        academicYearParam = academicYear;
      }

      const response = await api.get(`/grades/user/${userId}/subject/${subjectId}/average`, {
        params: { 
          semester: semesterName, 
          academicYear: academicYearParam 
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching subject average for user ${userId}, subject ${subjectId}:`, error);
      throw error;
    }
  },

  getSemesterSubjectAverages: async (userId, semester, academicYear) => {
    try {
      // If semester is an object, extract the name and schoolYear
      let semesterName, academicYearParam;
      if (typeof semester === 'object' && semester !== null) {
        semesterName = semester.name;
        academicYearParam = semester.schoolYear || academicYear;
      } else {
        semesterName = semester;
        academicYearParam = academicYear;
      }

      const response = await api.get(`/grades/user/${userId}/semester/averages`, {
        params: { 
          semester: semesterName, 
          academicYear: academicYearParam 
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching semester subject averages for user ${userId}:`, error);
      throw error;
    }
  },

  getSemesterAverage: async (userId, semester, academicYear) => {
    try {
      // If semester is an object, extract the name and schoolYear
      let semesterName, academicYearParam;
      if (typeof semester === 'object' && semester !== null) {
        semesterName = semester.name;
        academicYearParam = semester.schoolYear || academicYear;
      } else {
        semesterName = semester;
        academicYearParam = academicYear;
      }

      const response = await api.get(`/grades/user/${userId}/semester/average`, {
        params: { 
          semester: semesterName, 
          academicYear: academicYearParam 
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching semester average for user ${userId}:`, error);
      throw error;
    }
  },

  // US16: Class Overview for Instructors
  getClassStatistics: async (classId, semester, academicYear) => {
    try {
      // If semester is an object, extract the name and schoolYear
      let semesterName, academicYearParam;
      if (typeof semester === 'object' && semester !== null) {
        semesterName = semester.name;
        academicYearParam = semester.schoolYear || academicYear;
      } else {
        semesterName = semester;
        academicYearParam = academicYear;
      }

      const response = await api.get(`/grades/class/${classId}/statistics`, {
        params: { 
          semester: semesterName, 
          academicYear: academicYearParam 
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching class statistics for class ${classId}:`, error);
      throw error;
    }
  },

  compareSemesters: async (classId, semesters, academicYear) => {
    try {
      const response = await api.get(`/grades/class/${classId}/comparison`, {
        params: { semesters, academicYear }
      });
      return response.data;
    } catch (error) {
      console.error(`Error comparing semesters for class ${classId}:`, error);
      throw error;
    }
  },

  // Get grades by user and semester
  getGradesByUserAndSemester: async (userId, semester, academicYear) => {
    try {
      
      // If semester is an object, extract the name and schoolYear
      let semesterName, academicYearParam;
      if (typeof semester === 'object' && semester !== null) {
        semesterName = semester.name;
        academicYearParam = semester.schoolYear || academicYear;
      } else {
        semesterName = semester;
        academicYearParam = academicYear;
      }

      const requestUrl = `/grades/user/${userId}/semester`;
      const requestParams = { 
        semester: semesterName, 
        academicYear: academicYearParam 
      };
      
      const response = await api.get(requestUrl, { params: requestParams });
      
      return response.data;
    } catch (error) {
      console.error(`❌ Error fetching grades for user ${userId} and semester ${semester}:`, error);
      console.log("📊 Error details:", {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message
      });
      throw error;
    }
  },

  getSemesterConfirmation: async (semesterId, learnerId = null, classId = null) => {
    try {
      const params = {};
      if (learnerId) {
        params.learnerId = learnerId;
      }
      if (classId) {
        params.classId = classId;
      }
      const response = await api.get(`/grades/semester-confirmations/${semesterId}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching semester confirmation for semester ${semesterId}:`, error);
      throw error;
    }
  },

  getSemesterConfirmations: async (semesterIds = [], learnerId = null, classId = null) => {
    try {
      const params = {};
      if (Array.isArray(semesterIds) && semesterIds.length > 0) {
        params.semesterIds = semesterIds;
      }
      if (learnerId) {
        params.learnerId = learnerId;
      }
      if (classId) {
        params.classId = classId;
      }

      const response = await api.get('/grades/semester-confirmations', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching semester confirmations:', error);
      throw error;
    }
  },

  getMyPendingRejections: async () => {
    try {
      const response = await api.get('/grades/semester-confirmations/pending-rejections');
      return response.data || [];
    } catch (error) {
      // 403 just means the caller is not a learner - anything else is worth seeing.
      if (error.response?.status !== 403) {
        console.error('Error fetching pending rejections:', error.response?.status, error);
      }
      return [];
    }
  },

  confirmSemesterGrade: async (semesterId, payload) => {
    try {
      const response = await api.post(`/grades/semester-confirmations/${semesterId}/confirm`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error confirming semester grade for semester ${semesterId}:`, error);
      throw error;
    }
  },

  getSemesterDefinitiveGrades: async (semesterId, learnerId = null, classId = null) => {
    try {
      const params = {};
      if (learnerId) {
        params.learnerId = learnerId;
      }
      if (classId) {
        params.classId = classId;
      }
      const response = await api.get(`/grades/semester-definitive/${semesterId}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching definitive grades for semester ${semesterId}:`, error);
      throw error;
    }
  },

  updateSemesterDefinitiveGrades: async (semesterId, learnerId, classId, subjects) => {
    try {
      const response = await api.put(`/grades/semester-definitive/${semesterId}`, {
        learnerId,
        classId,
        subjects,
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating definitive grades for semester ${semesterId}:`, error);
      throw error;
    }
  },
};

export default gradeService;
